import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ANALYSIS_CACHE_MAX_ENTRIES,
  ANALYSIS_CACHE_MAX_PINNED,
  db,
  listExports,
  setAnalysisPinned,
  type SavedAnalysis,
  type SavedExport
} from "../../db";
import { useToastStore } from "../../stores/toast-store";
import { HelpButton } from "../help/HelpDrawer";
import { buildRunHistory, summarizeHistory, type RunHistoryRow } from "./history";

/**
 * Every run this browser still holds.
 *
 * The cache is bounded, so this is "what is still here", never "everything that
 * ever ran" — stated on the page, because a history that quietly forgets would
 * be read as a complete record.
 */
export function RunsPage() {
  const [rows, setRows] = useState<RunHistoryRow[] | null>(null);
  const [pinned, setPinned] = useState<Set<string>>(new Set());
  const [usage, setUsage] = useState<string | null>(null);
  const [reloads, setReloads] = useState(0);
  const showToast = useToastStore((state) => state.showToast);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([
      db.analyses.toArray().catch(() => [] as SavedAnalysis[]),
      db.decisions.toArray().catch(() => [] as Array<{ analysisKey: string }>),
      listExports().catch(() => [] as SavedExport[])
    ]).then(([analyses, decisions, exports]) => {
      if (cancelled) return;
      setRows(buildRunHistory({ analyses: analyses as never, decisions, exports }));
      setPinned(new Set(analyses.filter((row) => row.keep === true).map((row) => row.analysisKey)));
    });
    return () => {
      cancelled = true;
    };
    // `reloads` is a trigger, not a value this effect reads: bumping it is how
    // pinning and unpinning ask for a fresh read.
    // oxlint-disable-next-line react/exhaustive-effect-dependencies
  }, [reloads]);

  // Real numbers from the browser rather than an estimate of our own: the quota
  // that matters is the one the browser will enforce, and summing row sizes
  // here would mean serialising several megabytes to display a figure.
  useEffect(() => {
    let cancelled = false;
    if (typeof navigator === "undefined" || navigator.storage?.estimate === undefined) return;
    void navigator.storage
      .estimate()
      .then((estimate) => {
        if (cancelled || estimate.usage === undefined) return;
        setUsage(
          estimate.quota === undefined
            ? `${formatBytes(estimate.usage)} used`
            : `${formatBytes(estimate.usage)} of about ${formatBytes(estimate.quota)} used`
        );
      })
      .catch(() => {
        // Storage estimates are a nicety; their absence is not worth a message.
      });
    return () => {
      cancelled = true;
    };
    // Same trigger: a pin change should re-read the usage figure too.
    // oxlint-disable-next-line react/exhaustive-effect-dependencies
  }, [reloads]);

  const togglePin = async (analysisKey: string) => {
    const next = !pinned.has(analysisKey);
    const result = await setAnalysisPinned(analysisKey, next);
    if (!result.ok) {
      showToast(result.reason, "warning");
      return;
    }
    setReloads((value) => value + 1);
  };

  if (rows === null) {
    return <main className="p-6 text-sm text-slate-600">Reading this browser's run cache…</main>;
  }

  const counts = summarizeHistory(rows);

  return (
    <main className="mx-auto max-w-6xl space-y-4 p-6" data-testid="runs-page">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Runs</h1>
          <p className="text-sm text-slate-600" data-testid="runs-summary">
            {counts.total === 0
              ? "No runs are cached in this browser."
              : `${counts.total} cached · ${counts.quarantined} quarantined · ${counts.withDecisions} with decisions · ${counts.exported} exported`}
          </p>
          <p className="text-xs text-slate-600" data-testid="runs-cache-usage">
            The cache holds up to {ANALYSIS_CACHE_MAX_ENTRIES} runs, of which {ANALYSIS_CACHE_MAX_PINNED} may
            be kept against eviction. {pinned.size} kept.
            {usage === null ? "" : ` Browser storage: ${usage}.`}
          </p>
        </div>
        <HelpButton topicId="runs" label="Help for runs" />
      </header>

      {rows.length === 0 ? (
        <p className="rounded-sm border border-slate-300 bg-white p-4 text-sm text-slate-700" data-testid="runs-empty">
          Nothing has been analysed in this browser yet, or the cache has been cleared.{" "}
          <Link className="text-sky-700 underline" to="/">
            Run an analysis
          </Link>{" "}
          to start one.
        </p>
      ) : (
        <>
          <p className="text-xs text-slate-600" data-testid="runs-caveat">
            This is what this browser still holds. The cache is bounded and the oldest runs are
            dropped as new ones arrive, so a run missing here is not a run that never happened.
          </p>
          <div className="overflow-x-auto rounded-sm border bg-white">
            <table className="w-full text-left text-sm">
              <thead className="border-b bg-slate-50 text-xs uppercase text-slate-600">
                <tr>
                  <th className="p-2">Run</th>
                  <th className="p-2">Source</th>
                  <th className="p-2">Files</th>
                  <th className="p-2">Verdict</th>
                  <th className="p-2 text-right">Findings</th>
                  <th className="p-2 text-right">Decisions</th>
                  <th className="p-2">Exported</th>
                  <th className="p-2">Keep</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.analysisKey} className="border-b last:border-b-0" data-testid={`run-row-${row.analysisKey}`}>
                    <td className="p-2 align-top">
                      <Link
                        className="text-sky-700 underline"
                        to={`/results?run=${encodeURIComponent(row.analysisKey)}&tab=overview`}
                      >
                        {formatWhen(row.ranAt)}
                      </Link>
                      {row.ranAtIsCacheTime ? (
                        <span className="block text-xs text-slate-500">time cached, not run</span>
                      ) : null}
                    </td>
                    <td className="p-2 align-top text-xs">
                      {row.profileId === null ? (
                        <span className="text-slate-500">not recorded</span>
                      ) : (
                        <>
                          {row.profileId}
                          {row.profileVersion === null ? "" : ` · v${row.profileVersion}`}
                        </>
                      )}
                    </td>
                    <td className="p-2 align-top text-xs text-slate-600">
                      <span className="block truncate">{row.baselineFileName}</span>
                      <span className="block truncate">{row.latestFileName}</span>
                    </td>
                    <td className="p-2 align-top">
                      <span className={`rounded-sm border px-1.5 py-0.5 text-xs font-medium ${verdictTone(row.verdict)}`}>
                        {row.verdict}
                      </span>
                    </td>
                    <td className="p-2 text-right align-top tabular-nums">{row.findingCount}</td>
                    <td className="p-2 text-right align-top tabular-nums">{row.decisionCount}</td>
                    <td className="p-2 align-top text-xs">
                      {row.exported ? "yes" : <span className="text-slate-500">no</span>}
                    </td>
                    <td className="p-2 align-top">
                      <button
                        type="button"
                        className="rounded-sm border border-slate-300 px-2 py-0.5 text-xs hover:bg-slate-50"
                        data-testid={`pin-${row.analysisKey}`}
                        aria-pressed={pinned.has(row.analysisKey)}
                        onClick={() => void togglePin(row.analysisKey)}
                      >
                        {pinned.has(row.analysisKey) ? "Kept" : "Keep"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </main>
  );
}

function verdictTone(verdict: RunHistoryRow["verdict"]): string {
  if (verdict === "Quarantined") return "border-red-300 bg-red-50 text-red-800";
  if (verdict === "Warning") return "border-amber-300 bg-amber-50 text-amber-800";
  return "border-green-300 bg-green-50 text-green-800";
}

/** Bytes at a glance; the exact figure is not the point. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const mb = bytes / (1024 * 1024);
  return mb < 1 ? `${(bytes / 1024).toFixed(0)} kB` : `${mb.toFixed(1)} MB`;
}

function formatWhen(iso: string): string {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? iso : at.toLocaleString();
}
